/* @flow */

import ContentServiceClient from "../clients/ContentServiceClient";
import { trackContentSearchResult } from "../utils/SearchTrackingUtil";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import { List } from "immutable";
import * as _ from "lodash";

import ContentSearchActions from "../actions/ContentSearchActions";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";

import { Store } from "shared/src/stores/Store";
import { DEFAULT_ROWS } from "../utils/SearchUtil";

import { SearchResult, SearchResultItem } from "../datatypes/ContentSearch";

export type ContentSearchStoreState = {
    items: List<SearchResultItem> | undefined | null;
    numFound: number;
    page: number | undefined | null;
    query: string | undefined | null;
    loading: boolean;
    error: boolean;
};

let state: ContentSearchStoreState;

class ContentSearchStore extends Store {
    constructor() {
        super();
        state = this.getEmptyState();
        // Do not perform content search if we are not showing the result anyway.
        if (CountryUtil.getOptionalConfig().showContentSearchTab) {
            this.listenTo(ContentSearchActions.search, this.search as any);
        }
    }

    search(query: string, numPagesContent: number) {
        if (query === "") {
            return;
        }
        if (state.query === query && numPagesContent === state.page) {
            return;
        }
        const locale: string = SessionStore.getLocale();
        const country: string = SessionStore.getCountry();

        const isNewSearch =
            state.query !== query || !state.page || !(numPagesContent > 1) || numPagesContent === state.page;

        const page = isNewSearch ? 1 : numPagesContent;
        const rows = isNewSearch ? numPagesContent * DEFAULT_ROWS : DEFAULT_ROWS;

        if (!state.loading) {
            state.loading = true;
            this.trigger();
            ContentServiceClient.getSearchContent(country, locale, query, rows, page)
                .then((response) => this.searchSuccess(response, query, page, isNewSearch))
                .catch((error) => this.searchFail(error));
        }
    }

    searchSuccess(response: SearchResult, query: string, page: number, isNewSearch: boolean) {
        const newItems = this.calculateItems(query, page, response);
        state = {
            items: newItems,
            numFound: response.numFound,
            page: page,
            query: query,
            loading: false,
            error: false
        };
        if (isNewSearch) {
            // Content search pages start at 0
            trackContentSearchResult(query, 0, response.numFound);
        }
        this.trigger();
    }

    searchFail(error: any) {
        TrackingUtil.trackUiErrorSd("ContentSearch", "Loading of content search result  failed.", error);
        state.loading = false;
        state.error = true;
        state.query = undefined;
        this.trigger();
    }

    getEmptyState(): ContentSearchStoreState {
        return {
            items: undefined,
            numFound: 0,
            query: undefined,
            page: null,
            loading: false,
            error: false
        };
    }

    calculateItems(query: string | undefined | null, page: number, response: SearchResult): List<SearchResultItem> {
        const currentItems = _.get(response, ["results"], List() as List<SearchResultItem>);
        if (query === state.query && state.items && page > 1) {
            return List(state.items.concat(currentItems));
        } else {
            return List(currentItems);
        }
    }

    getState() {
        return state;
    }

    /**
     * For testing purpose.
     */
    resetData() {
        state = this.getEmptyState();
    }
}

export default new ContentSearchStore();
